/**
 * Folder Breadcrumb Component
 * Displays the current folder path with clickable navigation
 */

import { html, attr, Signal, ForEach, on, When } from '@tempots/dom'
import { Icon } from '@tempots/beatui'
import type { FolderResponse } from '@/services/types'

// ============================================================================
// Types
// ============================================================================

export interface FolderBreadcrumbProps {
  /** Current folder path (from root to current folder) */
  path: Signal<FolderResponse[]>
  /** Callback when a breadcrumb item is clicked */
  onNavigate: (folderId: string) => void
}

// ============================================================================
// Folder Breadcrumb Component
// ============================================================================

export function FolderBreadcrumb(props: FolderBreadcrumbProps) {
  const { path, onNavigate } = props

  return html.nav(
    attr.class('flex items-center gap-1 text-sm'),
    // Root/Home link
    html.button(
      attr.class(
        'flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 text-gray-600 hover:text-gray-900'
      ),
      on.click(() => onNavigate('')),
      Icon({ icon: 'mdi:home' }, attr.class('w-4 h-4')),
      html.span('Files')
    ),
    // Path segments
    ForEach(path, (folder, position) => {
      return html.span(
        attr.class('flex items-center'),
        // Separator
        Icon(
          { icon: 'mdi:chevron-right' },
          attr.class('w-4 h-4 text-gray-400 flex-shrink-0')
        ),
        // Folder link (last item is not clickable, shown as current)
        When(
          position.isLast,
          () =>
            html.span(
              attr.class('px-2 py-1 font-medium text-gray-900'),
              folder.map((f) => f.name)
            ),
          () =>
            html.button(
              attr.class(
                'px-2 py-1 rounded hover:bg-gray-100 text-gray-600 hover:text-gray-900'
              ),
              on.click(() => onNavigate(folder.get().id)),
              folder.map((f) => f.name)
            )
        )
      )
    })
  )
}
